/**
 * 社区分类数据管理 Hook
 * 使用 React Query 进行数据缓存和共享
 */
import { useQuery } from '@tanstack/react-query';
import { communityApi } from '@/api';
import { MessageSquare, Eye, Zap, Layers, TrendingUp, Bug, Calendar, HelpCircle, Lightbulb, Users, Star, LucideIcon } from 'lucide-react';

// 图标映射函数
const getIconComponent = (iconName: string): LucideIcon => {
  const iconMap: Record<string, LucideIcon> = {
    'MessageSquare': MessageSquare,
    'Eye': Eye,
    'Zap': Zap,
    'Layers': Layers,
    'TrendingUp': TrendingUp,
    'Bug': Bug,
    'Calendar': Calendar,
    'HelpCircle': HelpCircle,
    'Lightbulb': Lightbulb,
    'Users': Users,
    'Star': Star,
  };
  
  return iconMap[iconName] || MessageSquare;
};

// 分类数据类型
export interface CategoryItem {
  dictValue: string;
  dictLabel: string;
  count: number;
  color: string;
  icon: LucideIcon; // React Component
  iconName?: string; // 原始图标名称
}

// 获取社区分类数据
const fetchCommunityCategories = async (): Promise<CategoryItem[]> => {
  try {
    const response = await communityApi.getTopicTypes('community_topic_type');
    
    // 默认的"全部讨论"分类
    const defaultCategory: CategoryItem = {
      dictValue: "-1",
      dictLabel: "全部讨论",
      count: 118,
      color: "bg-gray-100 text-gray-700",
      icon: MessageSquare,
      iconName: "MessageSquare"
    };

    if (response && response.data && response.data.length) {
      const categories = response.data.map(item => ({
        dictValue: item.dictValue,
        dictLabel: item.dictLabel,
        count: item.count || 0,
        color: item.color || "bg-gray-100 text-gray-700",
        icon: item.cssClass ? getIconComponent(item.cssClass) : MessageSquare,
        iconName: item.cssClass || "MessageSquare"
      }));

      return [defaultCategory, ...categories];
    }

    return [defaultCategory];
  } catch (error) {
    console.error("获取社区分类失败:", error);
    // 返回默认分类
    return [{
      dictValue: "-1",
      dictLabel: "全部讨论",
      count: 118,
      color: "bg-gray-100 text-gray-700",
      icon: MessageSquare,
      iconName: "MessageSquare"
    }];
  }
};

/**
 * 使用社区分类数据的 Hook
 * 
 * @returns {Object} 包含分类数据和加载状态
 * - categories: 分类列表
 * - isLoading: 是否正在加载
 * - error: 错误信息
 * - refetch: 重新获取数据的函数
 */
export const useCommunityCategories = () => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['communityCategories'],
    queryFn: fetchCommunityCategories,
    staleTime: 5 * 60 * 1000, // 5分钟内数据视为新鲜
    refetchOnWindowFocus: false, // 窗口聚焦时不自动重新获取
  });

  return {
    categories: data || [
      {
        dictValue: "-1",
        dictLabel: "全部讨论",
        count: 118,
        color: "bg-gray-100 text-gray-700",
        icon: MessageSquare,
        iconName: "MessageSquare"
      }],
    isLoading,
    error,
    refetch
  };
};

/**
 * 根据 dictValue 获取分类信息
 */
export const useCategoryInfo = (categoryId: string) => {
  const { categories } = useCommunityCategories();
  
  return categories.find(cat => cat.dictValue === categoryId) || categories[0];
};
